import { useEffect, useMemo, useRef } from "react";

import { calculateRevealCount } from "../utils/siteUtils";

const useBlurReveal = (lines) => {
  const containerRef = useRef(null);
  const revealedCountRef = useRef(0);

  const wordGroups = useMemo(
    () => lines.map((line) => line.split(" ").filter(Boolean)),
    [lines]
  );

  const totalWords = useMemo(
    () => wordGroups.reduce((sum, group) => sum + group.length, 0),
    [wordGroups]
  );

  // Apply revealed state directly to DOM — avoids React re-renders on every scroll frame.
  const applyRevealState = (count) => {
    if (!containerRef.current) return;
    const words = containerRef.current.querySelectorAll(".reveal-word");
    words.forEach((word, index) => {
      word.classList.toggle("revealed", index < count);
    });
  };

  useEffect(() => {
    let rafId = null;

    const compute = () => {
      if (!containerRef.current) return;
      const bounds = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startY = windowHeight * 0.9;
      const endY = windowHeight * 0.3;
      const progress = (startY - bounds.top) / (startY - endY);
      const newCount = calculateRevealCount(progress, totalWords);

      if (newCount !== revealedCountRef.current) {
        revealedCountRef.current = newCount;
        applyRevealState(newCount);
      }
    };

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        compute();
        rafId = null;
      });
    };

    compute();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [totalWords]); // eslint-disable-line react-hooks/exhaustive-deps

  return { containerRef, wordGroups };
};

export default useBlurReveal;
